import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { driverKeys } from "@/features/drivers/api";
import type { DriverDetail } from "@/features/drivers/types";
import type { DocumentMutationResponse, DocumentsResponse, OcrStatusInfo } from "./types";

// Kalit "driver" bilan boshlanadi: real-time'dagi driver hodisasi (lib/realtime.ts) bu ro'yxatni ham yangilaydi
export const documentKeys = {
  list: (driverId: number | string) => ["driver", "documents", String(driverId)] as const,
};

const base = (driverId: number | string) => `/api/drivers/${driverId}/documents/`;

export function useDriverDocuments(driverId: number | string | undefined) {
  return useQuery({
    queryKey: documentKeys.list(driverId ?? ""),
    queryFn: () => api.get<DocumentsResponse>(base(driverId ?? ""), { query: { versions: 1 } }),
    enabled: !!driverId,
    placeholderData: keepPreviousData,
  });
}

/** Hujjat o'zgargach: ro'yxat, profil jamlanmasi, faollik lentasi va eslatmalar (so'rash eslatma yaratadi) */
function useAfterChange(driverId: number | string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: documentKeys.list(driverId) });
    void qc.invalidateQueries({ queryKey: driverKeys.detail(driverId) });
    void qc.invalidateQueries({ queryKey: driverKeys.activities(driverId) });
    void qc.invalidateQueries({ queryKey: driverKeys.reminders(driverId) });
    void qc.invalidateQueries({ queryKey: ["reminder"] });
  };
}

export interface UploadInput {
  kind: string;
  file: File;
  title?: string;
  issued_at?: string | null;
  expires_at?: string | null;
  state?: string;
  /** "Yangi versiya": shu nusxa arxivlanadi; bo'lmasa yangi nusxa mavjudlari yoniga qo'shiladi */
  replaces?: number;
}

export function useUploadDocument(driverId: number | string) {
  const done = useAfterChange(driverId);
  return useMutation({
    mutationFn: (input: UploadInput) => {
      const fd = new FormData();
      fd.set("kind", input.kind);
      fd.set("file", input.file, input.file.name);
      if (input.title) fd.set("title", input.title);
      if (input.issued_at) fd.set("issued_at", input.issued_at);
      if (input.expires_at) fd.set("expires_at", input.expires_at);
      if (input.state) fd.set("state", input.state);
      if (input.replaces) fd.set("replaces", String(input.replaces));
      return api.post<DocumentMutationResponse>(base(driverId), undefined, { formData: fd });
    },
    onSuccess: done,
  });
}

export function useRequestDocument(driverId: number | string) {
  const done = useAfterChange(driverId);
  return useMutation({
    mutationFn: (input: { kind: string; title?: string }) => api.post<DocumentMutationResponse>(`${base(driverId)}request/`, input),
    onSuccess: done,
  });
}

export interface UpdateInput {
  id: number;
  version: number;
  title?: string;
  issued_at?: string | null;
  expires_at?: string | null;
  state?: string;
}

export function useUpdateDocument(driverId: number | string) {
  const done = useAfterChange(driverId);
  return useMutation({
    mutationFn: ({ id, ...data }: UpdateInput) => api.patch<DocumentMutationResponse>(`${base(driverId)}${id}/`, data),
    onSuccess: done,
  });
}

export function useArchiveDocument(driverId: number | string) {
  const done = useAfterChange(driverId);
  return useMutation({
    mutationFn: (id: number) => api.delete<DocumentMutationResponse>(`${base(driverId)}${id}/`),
    onSuccess: done,
  });
}

export interface ApplyInput {
  id: number;
  fields: Record<string, unknown>;
  version: number;
}

export interface ApplyResponse extends DocumentMutationResponse {
  driver: DriverDetail;
  applied: string[];
  skipped: string[];
}

/** OCR taklifini qabul qilish (bo'sh `fields` -- rad etish): driver detaili javobdan keshga tushadi */
export function useApplyOcr(driverId: number | string) {
  const qc = useQueryClient();
  const done = useAfterChange(driverId);
  return useMutation({
    mutationFn: ({ id, ...data }: ApplyInput) => api.post<ApplyResponse>(`${base(driverId)}${id}/apply/`, data),
    onSuccess: (res) => {
      qc.setQueryData(driverKeys.detail(driverId), res.driver);
      void qc.invalidateQueries({ queryKey: driverKeys.all });
      done();
    },
  });
}

export function useRerunOcr(driverId: number | string) {
  const done = useAfterChange(driverId);
  return useMutation({
    mutationFn: (id: number) => api.post<DocumentMutationResponse>(`${base(driverId)}${id}/ocr/`),
    onSuccess: done,
  });
}

export function useOcrStatus() {
  return useQuery({
    queryKey: ["documents", "ocr-status"],
    queryFn: () => api.get<OcrStatusInfo>("/api/documents/ocr-status/"),
    staleTime: 30_000,
  });
}
